/**
 * Module 'approvals': human-in-the-loop for sessions Orgus launches, and for every session on a
 * machine with session control on (ADR 0001, amended). A session's hooks ask the machine agent,
 * the agent asks the hub, policy decides or a person does. Besides tool permissions this covers
 * Claude's questions (AskUserQuestion), plan approvals (ExitPlanMode) and MCP elicitations.
 */
import type { ContinuationOffer } from './continuation.ts';
import type { Id, Session, Ts } from '../index.ts';
import type { ApprovalDelegation, DelegatePolicy } from './approval-delegates.ts';
import type { PolicyRule } from './policy.ts';
import { isRegexSpace, trimEndOf } from './trim.ts';

/** 'deploy': a deploy, promote, rollback or CI deploy gate held by an environment approval policy (ADR 0048 point 8). */
export type ApprovalKind = 'tool' | 'question' | 'plan' | 'elicitation' | 'budget' | 'landing' | 'waiver' | 'deploy';
export type Risk = 'low' | 'medium' | 'high';
export type ApprovalAnswer = 'allow' | 'deny';
/** How far an "allow" reaches. 'project' writes a policy rule (audited, versioned). */
export type ApprovalScope = 'once' | 'session' | 'project';

export interface ApprovalRequest {
  id: Id;
  kind: ApprovalKind;
  machineId: Id;
  sessionId: Id;
  projectId: Id | null;
  missionId: Id | null;
  /** What the agent wants, in one line: "Run npm test", "Edit src/app.ts". */
  title: string;
  /** Tool name and the exact input (command, file path, diff). */
  tool?: string;
  input?: unknown;
  /** Why, when the agent said. */
  reason?: string;
  risk: Risk;
  /** What policy would normally say, and the rule id. */
  policyHint?: { decision: 'allow' | 'deny' | 'ask'; ruleId?: string; reason?: string };
  createdAt: Ts;
  /** When the timeout rule applies (default: deny after 15 minutes). */
  expiresAt: Ts;
  state: 'pending' | 'allowed' | 'denied' | 'expired';
  decidedBy?: string;
  /** The person whose saved choice Orgus applied. Absent on legacy records. */
  onBehalfOf?: string;
  decidedAt?: Ts;
  scope?: ApprovalScope;
  note?: string;
  /** Files sent with the note (module attachments, target approval:<id>). */
  attachmentIds?: Id[];
  /** The command or path policy matched against. */
  subject?: string;
  /** The session's folder. */
  cwd?: string;
  /** Verified session worktree, offered as an alternative to the asking file's folder. */
  worktree?: string;
  /** The pattern "Allow for session" and "Make it a rule" use unless the person edits it. */
  suggestedPattern?: string;
  /** Which device the decision came from ("Chrome on Windows"), or 'policy' / 'timeout'. */
  decidedFrom?: string;
  /** The rule a 'project' answer wrote, or the rule that decided automatically. */
  ruleId?: string;
  /** True when the request timed out and the session stopped to wait for a person. */
  parked?: boolean;
  /**
   * sha256 of the material input (tool plus the input without `description`), set by the hub
   * from the full input. A one-shot grant from Approve and resume matches on it.
   */
  fingerprint?: string;
  /** The session made another tool call after this was decided, so it did not stop on it. */
  laterWorkAt?: Ts;
  /**
   * A denial only: the session made another tool call after it (the first such call). The
   * denial still blocks (R-HL-3); this says the session carried on, so a redirect leads with
   * Mark as handled (approvalBlockLead).
   */
  carriedOnAt?: Ts;
  /** Approve and resume, when someone pressed it (R-HL-4). */
  resume?: ApprovalResume;
  /** "Mark as handled": the person says this no longer blocks anything. */
  dismissedAt?: Ts;
  dismissedBy?: string;
  /** Why Orgus marked it as handled on a person's behalf: MISSION_CLOSED_NOTE when closing its mission did. */
  dismissedNote?: string;
  /**
   * Handed back to the session (ADR 0056, Dismiss and snooze): a controlled session's request a
   * person passed back, so Claude Code's own dialog in its terminal decides. Never blocks.
   */
  handedBack?: boolean;
  /** The on-expiry policy in force when it was raised (R-HL-5). */
  onExpiry?: OnExpiry;
  /** How the asking session was launched (agents that say so): what the deny text may promise. */
  launchMode?: 'headless' | 'terminal';
  /** When the "expires soon" reminder goes out. */
  remindAt?: Ts;
  /** Hold mode: when the request was (or will be) escalated while the session keeps waiting. */
  escalateAt?: Ts;
  /**
   * 'session': a session Orgus did not launch (session control), or a question, plan or
   * elicitation from any session. If nobody answers by expiresAt, the request is withdrawn and
   * Claude Code asks in the session itself (never denied or parked).
   */
  fallback?: 'session';
  /**
   * Set when an approval policy needs more than one person (ADR 0048 point 8): the request runs
   * only once `approvals` reaches `required` people and policy and freezes still allow it.
   */
  quorum?: ApprovalQuorum;
  /** What Claude asks, for kinds 'question', 'plan' and 'elicitation'. */
  ask?: ApprovalAsk;
  /** What the person answered (answers, planMode or content), once decided; absent when empty. */
  askAnswer?: ApprovalAskAnswer;
  /**
   * A question or plan passed back to the session (fallback 'session') and then answered there,
   * read from the session's transcript (ADR 0056, answered in the session). The request stays
   * 'expired' in Orgus: this says what the session did with it.
   */
  sessionAnswer?: ApprovalSessionAnswer;
  /** Answered by a delegate agent under a project's delegate policy (ADR 0074); decidedBy is agent:<name>. */
  delegate?: ApprovalDelegation;
}

/** What the person at the session answered to a question or plan Orgus passed back (ADR 0056). */
export interface ApprovalSessionAnswer {
  /** answered: chose an answer, or approved the plan. declined: skipped the question, or kept Claude planning. */
  outcome: 'answered' | 'declined';
  /** The session's person: the owner of a personal machine, 'owner' on the hub's own machine, null when Orgus cannot tell. */
  by: string | null;
  /** When the session wrote the answer. */
  at: Ts;
  /** A question: each question's text to its answer (labels joined ", ", Other's free text as typed). Absent under content sharing off. */
  answers?: Record<string, string>;
  /** Declined: what the person told Claude. */
  note?: string;
  /** The transcript tool call it came from: one answer is recorded once. */
  toolUseId: Id;
}

/** One AskUserQuestion question, as Claude Code passes it (1 to 4 per call, 2 to 4 options each). */
export interface AskQuestion {
  question: string;
  /** Short chip text, at most 12 characters. */
  header: string;
  options: { label: string; description?: string }[];
  multiSelect: boolean;
}

/** One field of an MCP elicitation form: flat primitives only (the MCP spec's restricted schema). */
export interface ElicitationField {
  type: 'string' | 'number' | 'integer' | 'boolean';
  title?: string;
  description?: string;
  /** A choice: the values, and their display names in the same order. */
  enum?: string[];
  enumNames?: string[];
  default?: string | number | boolean;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  /** 'email', 'uri', 'date', 'date-time'. */
  format?: string;
}

export interface ElicitationSchema {
  properties: Record<string, ElicitationField>;
  required?: string[];
}

/** What Claude asks a person, beyond a tool permission. */
export type ApprovalAsk =
  /** AskUserQuestion: answered with `answers`. */
  | { type: 'questions'; questions: AskQuestion[] }
  /** ExitPlanMode: approved (optionally into another permission mode) or sent back with a note. */
  | { type: 'plan'; plan: string; planFilePath?: string }
  /** An MCP server asks for input: accepted with `content`, or declined. */
  | { type: 'elicitation'; server: string; message: string; mode: 'form' | 'url'; url?: string; schema?: ElicitationSchema };

/** The permission mode an approved plan continues in. */
export type PlanContinueMode = 'default' | 'acceptEdits';

/** An answer to a question, plan or elicitation, beyond allow and deny. */
export interface ApprovalAskAnswer {
  /** 'questions': question text to the chosen label; several labels joined with ", "; free text as is. */
  answers?: Record<string, string>;
  /** 'plan', with answer 'allow': the mode to continue in (default: the mode before planning). */
  planMode?: PlanContinueMode;
  /** 'elicitation', with answer 'allow': the form values, keyed by property name. */
  content?: Record<string, string | number | boolean>;
}

/**
 * The approvals a quorum has so far. People, not identities: two linked sign-ins of one member
 * count once. Agents and service accounts never count.
 */
export interface ApprovalQuorum {
  /** The approval policy (apl_<ulid>) that asked for it. */
  policyId: Id;
  required: number;
  /** Member ids (never an agent, a service account or a second identity of a member already here). */
  approvals: { by: string; at: Ts; from?: string }[];
  /** Who may not approve: the requester and, where the policy says so, the author. */
  excluded: string[];
  /** A one-person org: the owner approved their own request with a typed reason. */
  sod?: 'solo-owner';
  /** The typed reason, required with sod 'solo-owner'. */
  sodReason?: string;
}

/** An "Allow for session" answer: later requests in that session matching it are allowed. */
export interface SessionAllow {
  sessionId: Id;
  machineId: Id;
  tool: string;
  pattern: string;
  /** A worktree grant must also contain the resolved file path. */
  worktree?: string;
  by: string;
  at: Ts;
}

/**
 * Body of POST /api/approvals/session-allows/revoke (operator+, audited
 * `approval.revoke_session_allow`): take back one "Allow for session" answer. The session's
 * next matching request asks again. The four fields name the grant exactly.
 */
export interface SessionAllowRevoke {
  sessionId: Id;
  machineId: Id;
  tool: string;
  pattern: string;
}

export const SESSION_ALLOW_REVOKE_PATH = '/api/approvals/session-allows/revoke';

/**
 * POST /api/approvals/:id/hand-back (operator+, audited `approval.hand_back`): a controlled
 * session's held request goes back to the session, whose own dialog decides (ADR 0056). A
 * session Orgus launched has no dialog to hand back to: deny it with a note instead.
 */
export const approvalHandBackPath = (id: string): string => `/api/approvals/${encodeURIComponent(id)}/hand-back`;

/** state.ext.approvals */
export interface ApprovalsState {
  pending: ApprovalRequest[];
  /** Last 50 decided. */
  recent: ApprovalRequest[];
  sessionAllows?: SessionAllow[];
  /** Minutes before an unanswered request is denied: the org default of `expiry`. */
  timeoutMinutes?: number;
  /**
   * Decided requests that stopped a session and that nothing has cleared yet: expired (timeout
   * or the machine gave up), or denied by a person or by policy. Kept 7 days apart from
   * `recent`, so an old expiry never scrolls out and turns a mission back to Done (R-HL-3).
   */
  blocking?: ApprovalRequest[];
  /** Active (unused, unexpired) one-shot grants, for display. */
  grants?: ApprovalGrant[];
  /** What happens when nobody answers, per project (R-HL-5). */
  expiry?: ApprovalExpirySettings;
  /** Delegate policies, one per project at most, revoked ones included (ADR 0074). */
  delegates?: DelegatePolicy[];
  /** Who answers what rules leave open on this hub (ORGU-81). */
  answerer?: ApprovalAnswererSetting;
}

/**
 * Who answers what rules leave open (ORGU-81, ADR 0074): 'orgus' judges each such request at once
 * under its routine policy (@orgus/policy routineVerdict) and answers a mission's questions;
 * 'people' leaves them to a person, as before. An ask rule a person wrote always asks a person.
 */
export type ApprovalAnswerer = 'orgus' | 'people';
export const APPROVAL_ANSWERERS: readonly ApprovalAnswerer[] = ['orgus', 'people'];
export const APPROVAL_ANSWERER_LABEL: Record<ApprovalAnswerer, string> = { orgus: 'Orgus', people: 'People' };

/** GET (any signed-in user) and PUT (owner or admin) the hub's answerer. */
export const APPROVALS_ANSWERER_PATH = '/api/approvals/answerer';

export interface ApprovalAnswererSetting {
  answerer: ApprovalAnswerer;
  updatedBy?: string;
  updatedAt?: Ts;
}

/** decidedBy and decidedFrom of a request Orgus answered for the owner under its routine policy. */
export const ORGUS_DECIDER = 'orgus';

/** What Orgus answers a mission's question with, when the owner chose Orgus (ORGU-81). */
export const DECIDE_AND_CONTINUE = 'Decide and continue; the owner asked not to be asked.';

/** POST /api/approvals/:id */
export interface ApprovalDecision {
  answer: ApprovalAnswer;
  scope?: ApprovalScope;
  note?: string;
  /** For 'session' and 'project': the pattern to allow, default the suggested one. */
  pattern?: string;
  /** File requests only; the hub derives the worktree from its verified session. */
  pathScope?: 'folder' | 'worktree';
  /** Files that go with the note (uploaded first): copied to the session's machine, their paths added to the answer. */
  attachmentIds?: Id[];
  /**
   * For a question, plan or elicitation. A question is answered with answer 'allow' and
   * `answers`; 'deny' dismisses it (the note tells Claude why). A plan: 'allow' approves it,
   * 'deny' with a note keeps Claude planning. An elicitation: 'allow' with `content` accepts,
   * 'deny' declines.
   */
  ask?: ApprovalAskAnswer;
}

/** POST /api/approvals/batch: one answer for many requests (ADR 0072). */
export const APPROVALS_BATCH_PATH = '/api/approvals/batch';
/** At most this many ids in one batch. */
export const APPROVALS_BATCH_MAX = 100;

/**
 * POST /api/approvals/batch. Each id is checked and audited exactly as a single decision
 * (POST /api/approvals/:id); the answer is 200 with one result per id, in order.
 */
export interface ApprovalBatchDecision {
  ids: Id[];
  answer: ApprovalAnswer;
  /** 'once' (default) or 'session'. A rule ('project') is made from one request, never in bulk. */
  scope?: 'once' | 'session';
  /** One note for every request (a deny's reason, an allow's note). */
  note?: string;
  /** For scope 'session': the pattern per id; an id without one uses its suggested pattern. */
  patterns?: Record<Id, string>;
  /** The person confirmed allowing the high-risk requests in this batch. Without it each high-risk id is refused (409 'confirm-high-risk'). */
  confirmHighRisk?: boolean;
}

export interface ApprovalBatchItem {
  id: Id;
  ok: boolean;
  /** The decided request, when ok. */
  approval?: ApprovalRequest;
  /** HTTP status the same single decision would have returned, when not ok. */
  status?: number;
  error?: { what: string; why?: string; next?: string; code?: string };
}

export interface ApprovalBatchResult {
  batchId: string;
  results: ApprovalBatchItem[];
  /** How many were decided, and how many were refused. */
  decided: number;
  failed: number;
}

/**
 * The default wait before an unanswered request is denied (the org default of the expiry
 * policy, DEFAULT_EXPIRY_POLICY). Agents that do not take the hub's `wait` message give up
 * on their own a little after this.
 */
export const APPROVAL_TIMEOUT_MS = 15 * 60_000;
/** How many decided requests state.ext.approvals keeps. */
export const APPROVALS_RECENT_MAX = 50;

// ---------------------------------------------------------------------------
// Agent <-> hub messages (ExtMessage with ext 'approvals')
// ---------------------------------------------------------------------------

/** The Claude Code hook events a launched session sends to its agent. */
export type ApprovalHookEvent = 'PreToolUse' | 'PermissionRequest' | 'Elicitation' | 'Notification' | 'Stop';

/** agent -> hub, type 'request', with an id the answer echoes. Resent until answered. */
export interface ApprovalHookRequest {
  runId: string;
  sessionId: Id;
  /** 'Elicitation': tool is `mcp:<server>` and input the hook's elicitation fields. */
  event: 'PreToolUse' | 'PermissionRequest' | 'Elicitation';
  tool: string;
  input: unknown;
  cwd: string;
  permissionMode?: string;
  /** Set when a subagent made the call. */
  agentType?: string;
  /**
   * When the hook first asked (agent clock). Resends carry the same value, so the hub keeps the
   * original deadline across a hub restart instead of starting the clock again.
   */
  askedAt?: Ts;
  /** How the session was launched: headless (claude -p) or in a terminal. */
  launchMode?: 'headless' | 'terminal';
  /**
   * What to do when no rule decides and a person would be asked. 'wait' (the default) holds
   * the request for a person; 'pass' answers 'pass' at once, so a controlled session's own
   * permission flow runs (session control, PreToolUse).
   */
  ifAsk?: 'wait' | 'pass';
  /**
   * 'control': a session Orgus did not launch, reached through session control. The
   * default rules do not loosen it (only explicit rules and "Allow for session" allow);
   * Claude Code's own permission flow covers the rest.
   */
  source?: 'launch' | 'control';
  /** Session control: how long the hook waits for a person before the session asks itself. */
  waitMs?: number;
}

/** hub -> agent, type 'decision', same id as the request. 'pass': no decision (ifAsk: 'pass'). */
export interface ApprovalHookDecision {
  answer: ApprovalAnswer | 'pass';
  reason: string;
  approvalId?: Id;
  /** Stop the session's turn so it waits for a person (after a timeout). */
  park?: boolean;
  /** A person's answer to a question, plan or elicitation, for the hook to hand back. */
  ask?: ApprovalAskAnswer;
}

/** agent -> hub, type 'notice': a Notification or Stop hook from a launched session. */
export interface ApprovalNotice {
  runId: string;
  sessionId: Id;
  event: 'Notification' | 'Stop';
  message?: string;
  notificationType?: string;
  cwd?: string;
}

/**
 * agent -> hub, type 'lost' (ADR 0053, port takeover): the agent restarted and another program
 * held its approvals port, so the sessions it launched may be posting their hooks to that
 * program, which could answer "allow". Headless sessions the agent could prove it started (pid
 * and process start time) were stopped; the rest (terminal windows) need a person to stop them.
 */
export interface ApprovalLostRun {
  runId: string;
  /** Claude Code's session id, when the agent knew it. */
  sessionId?: Id;
  cwd: string;
  mode: 'headless' | 'terminal';
  /** The agent stopped it; false: it is still running (or could not be proven to be ours). */
  stopped: boolean;
}
export interface ApprovalLost {
  /** The approvals port another program holds. */
  port: number;
  runs: ApprovalLostRun[];
}

/** The console's line for a launched session the agent stopped after losing its approvals port. */
export const APPROVALS_STOPPED_TEXT = 'Orgus stopped this session: another program took its approvals port while Orgus restarted, so its approval checks could not be trusted. Start it again from Orgus.';
/** The console's line for a launched session still running after the agent lost its approvals port. */
export const APPROVALS_LOST_TEXT = "Approvals unreachable, stop this session: another program took Orgus's approvals port while Orgus restarted, so that program may be answering this session's approval checks.";

/** hub -> agent, type 'rules': the compiled rule set, cached for when the hub is unreachable. */
export interface ApprovalRulesPush {
  version: number;
  rules: PolicyRule[];
  /** The longest total wait any expiry policy allows (ms), so the agent can size hook timeouts. */
  waitMaxMs?: number;
}

/** agent -> hub, type 'rules.get': ask for the rule set, and say what this agent can do. */
export interface ApprovalRulesGet {
  caps?: ApprovalAgentCap[];
}

/**
 * What an agent announces in 'rules.get':
 *   wait     it takes { type: 'wait', data: { until } } and keeps the hook open until then
 *   expired  it reports requests it gave up on as { type: 'expired' }
 *   resume   session.start resumes a headless session (resumeSessionId)
 */
export type ApprovalAgentCap = 'wait' | 'expired' | 'resume';
export const APPROVAL_AGENT_CAPS: readonly ApprovalAgentCap[] = ['wait', 'expired', 'resume'];

/** hub -> agent, type 'wait', same id as the request: the hub answers by `until` at the latest. */
export interface ApprovalWait {
  until: Ts;
}

/** agent -> hub, type 'expired': a request the agent gave up on (the hub was unreachable). */
export type ApprovalExpiredReport = ApprovalHookRequest & { at: Ts };

/** One tool request a launched session made and how it was decided (for policy simulation). */
export interface ApprovalHistoryEntry {
  at: Ts;
  machineId: Id;
  sessionId: Id;
  projectId: Id | null;
  tool: string;
  subject?: string;
  cwd?: string;
  title: string;
  decision: 'allow' | 'ask' | 'deny';
  ruleId: string | null;
}

/** What other hub modules may ask the approvals module: ctx.ext<ApprovalsApi>('approvals'). */
export interface ApprovalsApi {
  /** Tool requests evaluated since `since`, oldest first (kept 7 days). */
  history(since: Ts): ApprovalHistoryEntry[];
  /** Requests waiting for a person, oldest first (missions read them to show a mission waiting). */
  pending(): ApprovalRequest[];
  /** One request, pending or recently decided (attachments check an approval note's files still have one). */
  get?(id: Id): ApprovalRequest | undefined;
  /** Decided requests that stopped a session (ApprovalsState.blocking). */
  blocking(): ApprovalRequest[];
  /**
   * A mission was closed (done or stopped): mark each of its blocking requests as handled, by
   * the person who closed it, with the note MISSION_CLOSED_NOTE, audited. A mission's requests
   * are the ones raised for it (missionId) or by a session it started, from that session's
   * machine. Returns the requests it marked. The missions module calls it on every close path,
   * so the hub, the Inbox, the badges and the needsYou selector agree (ADR 0056 section 6).
   */
  resolveForMission?(mission: MissionBlockScope, by: string, closedAs: 'done' | 'stopped'): ApprovalRequest[];
}

/** Which requests are a mission's, for resolveForMission: its id, and its sessions with their machines. */
export interface MissionBlockScope {
  id: Id;
  /** Each session the mission started or adopted, with the machine it runs on when known. */
  sessions: { sessionId: Id; machineId: Id | null }[];
}

/** The note on a request that closing its mission marked as handled. */
export const MISSION_CLOSED_NOTE = 'Mission closed';

// ---------------------------------------------------------------------------
// Outcomes: a decided request that stopped its session (R-HL-3)
// ---------------------------------------------------------------------------

export type ApprovalOutcome = 'expired' | 'denied';

/** Approve and resume, as recorded on the request it answered (R-HL-4). */
export interface ApprovalResume {
  by: string;
  at: Ts;
  /** Device, as decidedFrom. */
  from?: string;
  grantId: Id;
  /**
   * sent: the session was told to continue (`via`); live: the session was running (or cannot be
   * continued from here), only the grant was made; ran: the granted call came in and was
   * allowed; failed: the machine refused or was unreachable.
   */
  state: 'sent' | 'live' | 'ran' | 'failed';
  /**
   * sent only. message: a launched session still open got the go-ahead as its next message on
   * its input; restart: the ended session was started again headless (claude --resume).
   * Absent on records older than 2026-10-18 (they were restarts). fresh: the mission continued in
   * a new session from its handoff instead, cheaper than the cold context (ADR 0062 decision 2a).
   */
  via?: 'message' | 'restart' | 'fresh';
  /** What Approve and resume answered the person (every state), or why it failed. */
  message?: string;
}

/**
 * A one-shot grant from Approve and resume: the next call of exactly this tool and input in
 * this session is allowed once, for 30 minutes. Policy denies still win over it.
 */
export interface ApprovalGrant {
  /** grt_... */
  id: Id;
  approvalId: Id;
  machineId: Id;
  sessionId: Id;
  /** Compared case-insensitively. */
  tool: string;
  /** = the approval's fingerprint: the match is exact. */
  fingerprint: string;
  /** For display and audit. */
  subject?: string;
  by: string;
  at: Ts;
  expiresAt: Ts;
  usedAt?: Ts;
}

/** POST /api/approvals/:id/resume */
export interface ApprovalResumeRequest {
  note?: string;
}

/** The answer of POST /api/approvals/:id/resume. */
export interface ApprovalResumeResponse {
  approval: ApprovalRequest;
  grant: ApprovalGrant | null;
  /** True when the machine started the session again; false when only the grant was made. */
  resumed: boolean;
  message: string;
  /** The continue or fresh comparison for the session, when Orgus made one (ADR 0062 decision 2a). */
  continuation?: ContinuationOffer;
}

/** The fields the outcome helpers and deriveMission read (extra fields optional, so narrow objects fit). */
export type ApprovalLike = Pick<ApprovalRequest, 'id' | 'state' | 'missionId' | 'sessionId' | 'title' | 'createdAt' | 'machineId'> &
  Partial<Pick<ApprovalRequest, 'kind' | 'tool' | 'subject' | 'decidedAt' | 'decidedBy' | 'parked' | 'note' | 'ruleId' | 'laterWorkAt' | 'resume' | 'dismissedAt' | 'dismissedNote' | 'carriedOnAt'>>;

/**
 * blocked: needs a person. resuming: Approve and resume was sent under RESUME_GRACE_MS ago.
 * resume_failed: the last attempt failed. not_run: resumed, but the session ended without making the call.
 */
export type ApprovalBlockPhase = 'blocked' | 'resuming' | 'resume_failed' | 'not_run';

/**
 * What a blocker leads with (its primary action):
 *   resume   Approve and resume: expired, a bare denial by a person, or a resume that failed or did not run
 *   handled  Mark as handled: a person denied it with a note telling the session what to do
 *            instead (a redirect). Approving would run the call they turned down, so Approve and
 *            resume stays available behind a menu and a confirm. The block still stands (R-HL-3).
 *   none     neither: refused by policy (the rule is changed in Policies)
 */
export type ApprovalBlockLead = 'resume' | 'handled' | 'none';

/** A person's denial with a note: who, what they said, and whether the session made another tool call after it. */
export interface ApprovalRedirect {
  by: string;
  note: string;
  /** The session made another tool call after the denial (carriedOnAt). */
  carriedOn: boolean;
}

export interface ApprovalBlock {
  outcome: ApprovalOutcome;
  approval: ApprovalLike;
  phase: ApprovalBlockPhase;
  /** Approve and resume is offered: expired, or denied by a person (not by policy). */
  resumable: boolean;
  /** APPROVAL_BLOCK_LABEL: "Blocked: approval expired" | "Blocked: approval denied" | "Blocked: refused by policy". */
  label: string;
  /** One plain sentence for blockers and notifications. */
  title: string;
  /** The primary action (approvalBlockLead). */
  lead: ApprovalBlockLead;
  /** Set when a person redirected the session with a note. */
  redirect?: ApprovalRedirect;
}

/** How long after Approve and resume the session counts as resuming (not blocked). */
export const RESUME_GRACE_MS = 3 * 60_000;

/** Labels the hub (notifications) and the console (chips, cards) share, so they always agree. */
export const APPROVAL_BLOCK_LABEL = {
  expired: 'Blocked: approval expired',
  denied: 'Blocked: approval denied',
  policy: 'Blocked: refused by policy',
  resume_failed: 'Blocked: the resume did not start',
  not_run: 'Blocked: the approved call did not run',
} as const;

/**
 * The outcome of a decided request, if it is one that can stop a session: expired with the
 * session parked (timeout, or the machine gave up), or denied by a person or by policy.
 * Not: budget denies (the budget blocker covers them), withdrawals (decidedBy 'session').
 */
export function approvalOutcomeOf(a: ApprovalLike): ApprovalOutcome | null {
  if (a.state === 'expired') return a.parked && a.decidedBy !== 'session' ? 'expired' : null;
  // A question dismissed, a plan sent back or an elicitation declined is an answer, not a block;
  // so is Orgus's deny for the owner (ORGU-81): the session reads the reason and carries on.
  if (a.state === 'denied') return a.kind === 'budget' || a.decidedBy === 'budget' || a.decidedBy === ORGUS_DECIDER || a.kind === 'question' || a.kind === 'plan' || a.kind === 'elicitation' ? null : 'denied';
  return null;
}

/** Approve and resume is offered: expired, or denied by a person (policy denies are changed in Policies). */
export function approvalResumable(a: ApprovalLike): boolean {
  const o = approvalOutcomeOf(a);
  return o === 'expired' || (o === 'denied' && a.decidedBy !== 'policy');
}

/** decidedBy values that are Orgus itself, never a person: a denial by one of these is not a redirect. */
export const APPROVAL_SYSTEM_DECIDERS: ReadonlySet<string> = new Set(['policy', 'timeout', 'machine', 'session', 'budget', ORGUS_DECIDER]);

/**
 * A person denied it with a note: they told the session what to do instead (a redirect), not
 * only "no". Policy, timeouts, the machine, the session and budget stops never redirect.
 */
export function approvalRedirectOf(a: ApprovalLike): ApprovalRedirect | null {
  if (approvalOutcomeOf(a) !== 'denied') return null;
  const by = a.decidedBy?.trim();
  const note = a.note?.trim();
  if (!by || !note || APPROVAL_SYSTEM_DECIDERS.has(by)) return null;
  return { by, note, carriedOn: !!a.carriedOnAt };
}

/**
 * What a block leads with (ApprovalBlockLead). A redirect leads with Mark as handled while it
 * simply stands (phase 'blocked'); once someone pressed Approve and resume and it failed or did
 * not run, resuming is what they chose, so it leads again. Bare denials and expiries resume.
 */
export function approvalBlockLead(a: ApprovalLike, phase: ApprovalBlockPhase = 'blocked'): { lead: ApprovalBlockLead; redirect?: ApprovalRedirect } {
  if (!approvalResumable(a)) return { lead: 'none' };
  const redirect = approvalRedirectOf(a);
  if (redirect && phase === 'blocked') return { lead: 'handled', redirect };
  return { lead: 'resume', ...(redirect ? { redirect } : {}) };
}

/**
 * The one dim line under a redirect's Mark as handled. `me` is the signed-in person: their own
 * denial reads "You". `display` names a person as the console does ("the owner").
 */
export function redirectWhy(r: Pick<ApprovalRedirect, 'by' | 'carriedOn'>, me?: string | null, display?: (id: string) => string): string {
  const who = me && r.by === me ? 'You' : display ? display(r.by) : r.by;
  return `${who.charAt(0).toUpperCase()}${who.slice(1)} redirected it with a note, and the session ${r.carriedOn ? 'carried on' : 'ended its turn'}.`;
}

/** pending + blocking: the list deriveMission and the console pass around. Null without an approvals module. */
export function approvalsForDerive(s: Partial<ApprovalsState> | null | undefined): ApprovalRequest[] | null {
  if (!s || typeof s !== 'object') return null;
  if (!Array.isArray(s.pending) && !Array.isArray(s.blocking)) return null;
  const pending = Array.isArray(s.pending) ? s.pending : [];
  const blocking = Array.isArray(s.blocking) ? s.blocking : [];
  const ids = new Set(pending.map((a) => a.id));
  return [...pending, ...blocking.filter((a) => !ids.has(a.id))];
}

/** "15 minutes", "1 minute", "2 hours", "90 minutes". */
export function durationText(minutes: number): string {
  const m = Math.max(1, Math.round(minutes));
  if (m >= 60 && m % 60 === 0) {
    const h = m / 60;
    return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  }
  return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
}

const sentence = (t: string): string => trimEndOf(t.trim(), (ch) => ch === '.' || isRegexSpace(ch));

/** The one plain sentence a blocker, a notification and a card show. */
export function approvalBlockTitle(a: ApprovalLike, phase: ApprovalBlockPhase = 'blocked'): string {
  const title = sentence(a.title);
  if (phase === 'not_run') return `Blocked: the session resumed but did not run the approved call. ${title}.`;
  if (phase === 'resume_failed') return `Blocked: the resume did not start. ${sentence(a.resume?.message ?? 'The machine did not say why')}.`;
  if (phase === 'resuming') return `${a.resume?.via === 'message' ? 'Continuing' : a.resume?.via === 'fresh' ? 'Continuing fresh' : 'Resuming'}: ${title}. Approved by ${a.resume?.by ?? 'a person'}.`;
  // Approved while it was open, and it could not be told: say so, so the press visibly did something.
  if (a.resume?.state === 'live') return `Blocked: approved by ${a.resume.by}, but the session has not continued. It asked: ${title}.`;
  const outcome = approvalOutcomeOf(a);
  if (outcome === 'expired') {
    if (a.decidedBy === 'machine') return `Blocked: approval expired. It asked: ${title}. The machine lost contact with Orgus before an answer arrived.`;
    const mins = ((a.decidedAt ?? a.createdAt) - a.createdAt) / 60_000;
    return `Blocked: approval expired. It asked: ${title}. Nobody answered within ${durationText(mins)}.`;
  }
  if (a.decidedBy === 'policy') return `Blocked: refused by policy. ${title}: ${sentence(a.note ?? 'a policy rule refused it')}.`;
  return `Blocked: approval denied. ${a.decidedBy ?? 'Someone'} denied: ${title}.${a.note ? ` Note: ${a.note}` : ''}`;
}

/**
 * Why this session is stopped on an approval, or null. Pure: the hub and the console share it.
 *   null when the session is working or waiting (it moved on, or it is asking now), when a
 *   request of it is still pending (the pending path covers that), or when no decided request
 *   blocks it. Otherwise the newest of its blocking requests: expired with parked, or denied
 *   (not budget, not withdrawn), with no laterWorkAt, not dismissed, decided after the session
 *   started. A live idle session (an interactive terminal whose turn was stopped) is blocked too.
 *   A request from another machine than the session's never counts (pass session.machineId).
 */
export function approvalBlockFor(
  session: Pick<Session, 'id' | 'status' | 'startedAt' | 'lastActivity'> & Partial<Pick<Session, 'machineId'>>,
  approvals: readonly ApprovalLike[],
  now: number,
): ApprovalBlock | null {
  if (session.status === 'working' || session.status === 'waiting') return null;
  let best: ApprovalLike | null = null;
  for (const a of approvals) {
    if (a.sessionId !== session.id) continue;
    // Only the session's own machine can block it: another machine cannot report for it.
    if (session.machineId && a.machineId && a.machineId !== session.machineId) continue;
    if (a.state === 'pending') return null;
    if (!approvalOutcomeOf(a) || a.laterWorkAt || a.dismissedAt) continue;
    const at = a.decidedAt ?? a.createdAt;
    if (at < session.startedAt) continue;
    if (!best || at > (best.decidedAt ?? best.createdAt)) best = a;
  }
  if (!best) return null;
  const outcome = approvalOutcomeOf(best)!;
  const r = best.resume;
  let phase: ApprovalBlockPhase = 'blocked';
  if (r?.state === 'sent' && now - r.at < RESUME_GRACE_MS) phase = 'resuming';
  else if (r?.state === 'failed') phase = 'resume_failed';
  else if (r?.state === 'sent' && session.status === 'ended' && session.lastActivity > r.at) phase = 'not_run';
  const policy = outcome === 'denied' && best.decidedBy === 'policy';
  return {
    outcome,
    approval: best,
    phase,
    resumable: approvalResumable(best),
    label: outcome === 'expired' ? APPROVAL_BLOCK_LABEL.expired : policy ? APPROVAL_BLOCK_LABEL.policy : APPROVAL_BLOCK_LABEL.denied,
    title: approvalBlockTitle(best, phase),
    ...approvalBlockLead(best, phase),
  };
}

/**
 * The prompt Approve and resume sends to the resumed session (on stdin). Quotes the command
 * word for word so the session repeats it exactly and the one-shot grant matches.
 */
export function continuationPrompt(a: Pick<ApprovalRequest, 'state' | 'title'> & Partial<Pick<ApprovalRequest, 'tool' | 'subject'>>, by: string): string {
  const how = a.state === 'denied' ? 'was denied' : 'expired with no answer';
  const tool = a.tool || 'the tool';
  let what = (a.subject ?? a.title).trim();
  if (what.length > 2000) what = `${what.slice(0, 1999)}…`;
  const fence = '```';
  const asked = what.includes('\n') ? `${tool}:\n${fence}\n${what}\n${fence}` : `${tool} ${what}`;
  const run = tool.toLowerCase() === 'bash' ? 'Run exactly that now' : `Call ${tool} again with the same input now`;
  return `Orgus: ${by} approved the request that ${how} earlier:\n${asked}\nIt is allowed once. ${run}, then carry on with the task where you left off.`;
}

// ---------------------------------------------------------------------------
// Expiry policy: what happens when nobody answers (R-HL-5)
// ---------------------------------------------------------------------------

/** deny: answer no and mark the session blocked. hold: keep the session waiting, escalate, deny at the cap. */
export type OnExpiry = 'deny' | 'hold';

export interface ApprovalExpiryPolicy {
  /** Minutes a request waits before the on-expiry action (deny), or before the escalation (hold). 1..240. */
  timeoutMinutes: number;
  /**
   * deny: answer no, stop the turn, mark the session and mission blocked.
   * hold: keep the session waiting and escalate; deny as above at holdMaxMinutes.
   */
  onExpiry: OnExpiry;
  /** hold only: total wait cap. timeoutMinutes..720. */
  holdMaxMinutes: number;
  /** Notify this many minutes before the deny. 0 = off; must be shorter than the time to the deny. */
  remindBeforeMinutes: number;
}

/** GET and PUT /api/approvals/expiry; state.ext.approvals.expiry. */
export interface ApprovalExpirySettings {
  org: ApprovalExpiryPolicy;
  /** Per project: any field overrides the org default. */
  projects: Record<Id, Partial<ApprovalExpiryPolicy>>;
  updatedAt?: Ts;
  updatedBy?: string;
}

/** Safe default: deny after 15 minutes (Approve and resume loses nothing), remind at 10. */
export const DEFAULT_EXPIRY_POLICY: ApprovalExpiryPolicy = { timeoutMinutes: 15, onExpiry: 'deny', holdMaxMinutes: 120, remindBeforeMinutes: 5 };
export const EXPIRY_TIMEOUT_MAX_MINUTES = 240;
export const EXPIRY_HOLD_MAX_MINUTES = 720;

/** The policy in force for a project: defaults, then the org, then the project's overrides. */
export function expiryPolicyFor(s: ApprovalExpirySettings | undefined | null, projectId: Id | null): ApprovalExpiryPolicy {
  const proj = projectId && s?.projects ? s.projects[projectId] : undefined;
  return { ...DEFAULT_EXPIRY_POLICY, ...(s?.org ?? {}), ...(proj ?? {}) };
}

/** Minutes from the request to the deny: the timeout, or the hold cap. */
export function expiryDenyMinutes(p: ApprovalExpiryPolicy): number {
  return p.onExpiry === 'hold' ? p.holdMaxMinutes : p.timeoutMinutes;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const wholeIn = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;

/** A plain-language problem with a full expiry policy, or null. Shared by the hub and the console. */
export function expiryPolicyError(v: unknown): string | null {
  if (!isRecord(v)) return 'Send the expiry policy as a JSON object.';
  if (!wholeIn(v.timeoutMinutes, 1, EXPIRY_TIMEOUT_MAX_MINUTES)) return `The wait must be a whole number of minutes from 1 to ${EXPIRY_TIMEOUT_MAX_MINUTES}.`;
  if (v.onExpiry !== 'deny' && v.onExpiry !== 'hold') return 'On expiry must be deny (deny and mark blocked) or hold (keep waiting, then deny).';
  if (!wholeIn(v.holdMaxMinutes, 1, EXPIRY_HOLD_MAX_MINUTES)) return `The longest wait must be a whole number of minutes from 1 to ${EXPIRY_HOLD_MAX_MINUTES}.`;
  if (v.onExpiry === 'hold' && v.holdMaxMinutes < v.timeoutMinutes) {
    return `The longest wait must be at least the wait (${v.timeoutMinutes} minutes) and at most ${EXPIRY_HOLD_MAX_MINUTES} minutes.`;
  }
  const toDeny = v.onExpiry === 'hold' ? v.holdMaxMinutes : v.timeoutMinutes;
  if (!wholeIn(v.remindBeforeMinutes, 0, EXPIRY_HOLD_MAX_MINUTES) || (v.remindBeforeMinutes > 0 && v.remindBeforeMinutes >= toDeny)) {
    return `The reminder must be a whole number of minutes, 0 for none, and shorter than the time to the deny (${toDeny} minutes).`;
  }
  return null;
}

/** A problem with a whole settings body (org default plus project overrides), or null. */
export function expirySettingsError(v: unknown): string | null {
  if (!isRecord(v)) return 'Send the expiry settings as a JSON object.';
  const org = expiryPolicyError(v.org);
  if (org) return `Org default: ${org}`;
  if (v.projects !== undefined && !isRecord(v.projects)) return 'Project overrides must be an object keyed by project id.';
  const keys: readonly string[] = ['timeoutMinutes', 'onExpiry', 'holdMaxMinutes', 'remindBeforeMinutes'];
  for (const [id, o] of Object.entries((v.projects ?? {}) as Record<string, unknown>)) {
    if (!id || id.length > 200) return 'A project override needs a project id.';
    if (!isRecord(o)) return `Project ${id}: send the override as a JSON object.`;
    const extra = Object.keys(o).find((k) => !keys.includes(k));
    if (extra) return `Project ${id}: ${extra} is not an expiry setting.`;
    const merged = expiryPolicyError({ ...(v.org as object), ...o });
    if (merged) return `Project ${id}: ${merged}`;
  }
  return null;
}

/**
 * What a deny text may promise about what happens next (R-HL-5):
 *   headless     Approve and resume restarts this session with the call allowed, so it will be told
 *   interactive  a session open in a terminal: Orgus restarts nothing, a person tells it to continue
 *   none         Orgus cannot restart it (not launched by Orgus, or its agent cannot resume)
 */
export type DenyResumeMode = 'headless' | 'interactive' | 'none';

/** The last sentence of a deny text: what a person can do, and how the session hears of it. */
function whatNext(resume: DenyResumeMode): string {
  if (resume === 'headless') return 'a person can approve and resume it from Orgus, and this session will then be told to run it.';
  if (resume === 'interactive') return 'a person can approve it in Orgus and tell you to continue. Continue only when someone tells you to.';
  return 'a person can approve it in Orgus, and this session continues only when someone tells it to.';
}

/** What the agent is told when nobody answered (true about what happens next; R-HL-5). */
export function expiryDenyText(p: ApprovalExpiryPolicy, ctx: { mission: boolean; minutes?: number; resume: DenyResumeMode }): string {
  const within = durationText(ctx.minutes ?? expiryDenyMinutes(p));
  return (
    `Nobody answered this approval request in Orgus within ${within}, so it was not allowed and did not run. ` +
    'Stop here: do not retry it or work around it, and end your turn with one line on where you are. ' +
    `Orgus marked this ${ctx.mission ? 'mission' : 'session'} blocked; ${whatNext(ctx.resume)}`
  );
}

/**
 * What the agent is told when a person denied the request. A denial keeps the session (and its
 * mission) blocked once the session stops, until the same call is allowed or a person marks it
 * handled (R-HL-3), so the text never suggests the work can still read as done without it.
 */
export function personDenyText(by: string, note: string, ctx: { mission: boolean; resume: DenyResumeMode }): string {
  const what = ctx.mission ? 'mission' : 'session';
  return (
    `${by} denied this in Orgus.${note ? ` Note: ${note}` : ''} Do not retry it or work around it. ` +
    'If you cannot finish without it, say so in one line and end your turn. ' +
    `Until a person approves it or marks it handled, Orgus shows this ${what} as blocked once this session stops; ${whatNext(ctx.resume)}`
  );
}

/** What an agent tells the session when it gave up waiting because the hub was unreachable. */
export const AGENT_GAVE_UP_TEXT =
  'This machine lost contact with Orgus, so no answer arrived in time and this did not run. Stop here and end your turn. ' +
  'Orgus marks this session blocked once it is back in contact, and a person can approve and resume it from Orgus.';

/**
 * An audit entry the activity feed leaves out: an allow Orgus gave by itself (a policy rule or an
 * earlier "Allow for session"). A controlled session gets one per tool call, so in the feed they
 * would bury what happened; the Audit page keeps every one.
 */
export function isRoutineApprovalAudit(e: { actor: string; action: string }): boolean {
  return e.action === 'approval.allow' && e.actor === 'policy';
}

/** Audit labels for the actions this module records (their `action` is not an ActionKind). */
export const APPROVALS_AUDIT_LABEL: Record<string, string> = {
  'approval.ask': 'Ask for approval',
  'approval.allow': 'Allow',
  'approval.deny': 'Deny',
  'approval.expire': 'Approval expired',
  'approval.hold': 'Kept waiting',
  'approval.resume': 'Approve and resume',
  'approval.dismiss': 'Mark as handled',
  'approval.hand_back': 'Hand a request back to its session',
  'approval.batch': 'Answer several requests at once',
  'approval.session_answer': 'Answered in the session',
  'approval.expiry_policy': 'Change approval expiry',
  'approval.answerer': 'Change who answers approval requests',
  'approval.answer_question': 'Orgus answered a mission question',
  'approval.revoke_session_allow': 'Take back an allow for a session',
  'approval.delegate_policy': 'Change a delegate policy',
  'approval.delegate_revoke': 'Revoke a delegate policy',
};
